import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Plus, CloudSun, Wind, Droplets, CloudRain, Thermometer,
  AlertTriangle, FlaskConical, Trash2, X, Wand2, Calendar as CalIcon, Sprout,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import FertilizationForm from "../components/FertilizationForm";
import { getPhenologyPhases } from "./Dashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const wmoCode = (t, c) => {
  if (c === 0) return t("vineyard.weatherClear");
  if (c <= 3) return t("vineyard.weatherPartlyCloudy");
  if (c <= 48) return t("vineyard.weatherFog");
  if (c <= 57) return t("vineyard.weatherLightRain");
  if (c <= 67) return t("vineyard.weatherRain");
  if (c <= 77) return t("vineyard.weatherSnow");
  if (c <= 82) return t("vineyard.weatherShowers");
  if (c <= 99) return t("vineyard.weatherStorm");
  return t("vineyard.weatherUnknown");
};

const dayName = (iso, locale) =>
  new Date(iso).toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });

const riskLabel = (t, s) => (s === 0 ? t("vineyard.riskLow") : s === 1 ? t("vineyard.riskModerate") : s === 2 ? t("vineyard.riskHigh") : t("vineyard.riskVeryHigh"));

export default function VineyardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const [vineyard, setVineyard] = useState(null);
  const [weather, setWeather] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFertForm, setShowFertForm] = useState(false);
  const isFR = vineyard?.country === "FR";

  const load = async () => {
    try {
      const [vRes, tRes] = await Promise.all([
        axios.get(`${API}/vineyards`, { withCredentials: true }),
        axios.get(`${API}/treatments?vineyard_id=${id}`, { withCredentials: true }),
      ]);
      const v = vRes.data.find((x) => x.id === id);
      if (!v) { navigate("/dashboard"); return; }
      setVineyard(v);
      setTreatments(tRes.data);
      // Load weather
      const w = await axios.get(`${API}/weather?lat=${v.latitude}&lon=${v.longitude}`, { withCredentials: true });
      setWeather(w.data);
    } catch (e) {
      console.error(e);
      toast.error(t("vineyard.loadError"));
    } finally { setLoading(false); }
  };

    // eslint-disable-next-line
  useEffect(() => { load(); }, [id]);

  const removeTreatment = async (tid) => {
    if (!window.confirm(t("vineyard.confirmDeleteTreatment"))) return;
    try {
      await axios.delete(`${API}/treatments/${tid}`, { withCredentials: true });
      setTreatments((prev) => prev.filter((x) => x.id !== tid));
      toast.success(t("vineyard.treatmentDeleted"));
    } catch { toast.error(t("vineyard.genericError")); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="vs-spinner" /></div>;
  if (!vineyard) return null;

  return (
    <div className="min-h-screen pb-24" data-testid="vineyard-detail-page">
      <Header title={vineyard.name} right={
        <button onClick={() => navigate("/dashboard")} className="p-2 rounded-lg"
          style={{ border: "2px solid var(--border)" }} aria-label={t("vineyard.backAriaLabel")} data-testid="back-btn">
          <ArrowLeft size={18} />
        </button>
      } />

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {/* Weather current */}
        {weather?.current && (
          <section className="vs-card p-5" style={{ background: "var(--surface)" }} data-testid="weather-current">
            <div className="flex items-center gap-2 mb-3">
              <CloudSun size={20} style={{ color: "var(--primary)" }} />
              <h3 className="heading text-lg font-bold">{t("vineyard.weatherTitle")}</h3>
              <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
                {wmoCode(t, weather.current.weather_code)}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Stat icon={Thermometer} label={t("vineyard.statTemp")} value={`${Math.round(weather.current.temperature_2m)}°C`} testid="stat-temp" />
              <Stat icon={Wind} label={t("vineyard.statWind")} value={`${Math.round(weather.current.wind_speed_10m)} km/h`} testid="stat-wind" />
              <Stat icon={Droplets} label={t("vineyard.statHumidity")} value={`${Math.round(weather.current.relative_humidity_2m)}%`} testid="stat-humid" />
              <Stat icon={CloudRain} label={t("vineyard.statRain")} value={`${weather.current.precipitation} mm`} testid="stat-rain" />
            </div>

            {/* External weather links */}
            <div>
              <div className="vs-label">{t("vineyard.otherWeatherApps")}</div>
              <div className="grid grid-cols-2 gap-2">
                {isFR ? (
                  <>
                    <ExternalWeatherLink
                      url="https://meteofrance.com"
                      label="Météo-France"
                      bg="#E63312"
                      testid="link-meteofrance"
                    />
                    <ExternalWeatherLink
                      url="https://www.infoclimat.fr"
                      label="Infoclimat"
                      bg="#0066B3"
                      testid="link-infoclimat"
                    />
                    <ExternalWeatherLink
                      url="https://vigilance.meteofrance.fr"
                      label="Vigilance Météo-France"
                      bg="#1A4480"
                      testid="link-vigilance"
                    />
                    <ExternalWeatherLink
                      url={`https://www.windy.com/?${vineyard.latitude},${vineyard.longitude},11`}
                      label={`Windy (${t("vineyard.statWind").toLowerCase()})`}
                      bg="#3F4B5B"
                      testid="link-windy"
                    />
                  </>
                ) : (
                  <>
                    <ExternalWeatherLink
                      url={`https://www.3bmeteo.com/meteo/${encodeURIComponent(extractComune(vineyard.location_name))}`}
                      label="3B Meteo"
                      bg="#0066B3"
                      testid="link-3bmeteo"
                    />
                    <ExternalWeatherLink
                      url={`https://www.ilmeteo.it/meteo/${encodeURIComponent(capitalize(extractComune(vineyard.location_name)))}`}
                      label="IlMeteo.it"
                      bg="#E63312"
                      testid="link-ilmeteo"
                    />
                    <ExternalWeatherLink
                      url={`https://www.meteoam.it/it/ricerca-localita?city=${encodeURIComponent(extractComune(vineyard.location_name))}`}
                      label="Aeronautica Militare"
                      bg="#1A4480"
                      testid="link-aeronautica"
                    />
                    <ExternalWeatherLink
                      url={`https://www.windy.com/?${vineyard.latitude},${vineyard.longitude},11`}
                      label={`Windy (${t("vineyard.statWind").toLowerCase()})`}
                      bg="#3F4B5B"
                      testid="link-windy"
                    />
                  </>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Disease risks */}
        {weather?.disease_risks && weather.disease_risks.length > 0 && (
          <section className="vs-card p-5" data-testid="disease-risks">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} style={{ color: "var(--accent)" }} />
              <h3 className="heading text-lg font-bold">{t("vineyard.diseaseRiskTitle")}</h3>
            </div>
            <div className="space-y-2">
              {weather.disease_risks.map((r) => (
                <div key={r.date} className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}>
                  <span className="font-semibold text-sm">{dayName(r.date, dateLocale)}</span>
                  <div className="flex gap-2">
                    <span className={`vs-pill risk-${r.peronospora}`} data-testid={`risk-perono-${r.date}`}>
                      {t("vineyard.diseasePeronospora")} · {riskLabel(t, r.peronospora)}
                    </span>
                    <span className={`vs-pill risk-${r.oidio}`} data-testid={`risk-oidio-${r.date}`}>
                      {t("vineyard.diseaseOidio")} · {riskLabel(t, r.oidio)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 7-day forecast */}
        {weather?.daily && (
          <section className="vs-card p-5" data-testid="forecast-7d">
            <h3 className="heading text-lg font-bold mb-3">{t("vineyard.forecastTitle")}</h3>
            <div className="grid grid-cols-1 gap-2">
              {weather.daily.time.map((d, i) => (
                <div key={d} className="flex items-center justify-between p-2 rounded-lg"
                  style={{ borderBottom: "1px solid var(--border)" }}>
                  <span className="text-sm font-semibold w-28">{dayName(d, dateLocale)}</span>
                  <span className="text-xs flex-1 px-2" style={{ color: "var(--text-muted)" }}>
                    {wmoCode(t, weather.daily.weather_code[i])}
                  </span>
                  <div className="flex items-center gap-3 text-sm">
                    <span title={t("vineyard.statRain")}><CloudRain size={14} className="inline mr-1" />{weather.daily.precipitation_sum[i]}mm</span>
                    <span title={t("vineyard.statWind")}><Wind size={14} className="inline mr-1" />{Math.round(weather.daily.wind_speed_10m_max[i])}km/h</span>
                    <span className="font-semibold">{Math.round(weather.daily.temperature_2m_min[i])}° / {Math.round(weather.daily.temperature_2m_max[i])}°</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Treatments */}
        <section className="vs-card p-5" data-testid="treatments-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FlaskConical size={20} style={{ color: "var(--primary)" }} />
              <h3 className="heading text-lg font-bold">{t("vineyard.treatmentsTitle")}</h3>
            </div>
            <button onClick={() => setShowForm(true)} className="vs-btn-primary inline-flex items-center gap-1 px-3 py-2 text-sm"
              data-testid="add-treatment-btn">
              <Plus size={16} /> {t("vineyard.newButton")}
            </button>
          </div>
          {treatments.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>
              {t("vineyard.noTreatments")}
            </p>
          ) : (
            <div className="space-y-3">
              {treatments.map((tr) => <TreatmentCard key={tr.id} t={tr} onDelete={() => removeTreatment(tr.id)} />)}
            </div>
          )}
        </section>

        {/* Quick add fertilization */}
        <section className="vs-card p-5" data-testid="fert-quick-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sprout size={20} style={{ color: "var(--primary)" }} />
              <div>
                <h3 className="heading text-lg font-bold">{t("vineyard.fertilizationTitle")}</h3>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {t("vineyard.fertilizationDesc")}
                </p>
              </div>
            </div>
            <button onClick={() => setShowFertForm(true)}
              className="vs-btn-primary inline-flex items-center gap-1 px-3 py-2 text-sm"
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
              data-testid="add-fertilization-btn">
              <Plus size={16} /> {t("vineyard.newFertButton")}
            </button>
          </div>
        </section>
      </main>

      {showForm && (
        <TreatmentForm
          vineyardId={id}
          vineyard={vineyard}
          onClose={() => setShowForm(false)}
          onCreated={(nt) => { setTreatments((arr) => [nt, ...arr]); setShowForm(false); }}
        />
      )}

      {showFertForm && (
        <FertilizationForm
          vineyardId={id}
          onClose={() => setShowFertForm(false)}
          onCreated={() => { toast.success(t("vineyard.fertSavedToast")); setShowFertForm(false); }}
        />
      )}

      <BottomNav />
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--secondary)", border: "1px solid var(--border)" }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} style={{ color: "var(--primary)" }} />
        <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</span>
      </div>
      <div className="heading text-xl font-bold">{value}</div>
    </div>
  );
}

function ExternalWeatherLink({ url, label, bg, testid }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="px-3 py-2.5 rounded-lg flex items-center justify-between gap-2 text-sm font-semibold transition-transform hover:scale-[1.02]"
      style={{ background: bg, color: "white", border: "2px solid transparent" }}
      data-testid={testid}
    >
      <span className="truncate">{label}</span>
      <ExternalLink size={14} className="shrink-0" />
    </a>
  );
}

// Extract comune (first word) from location_name for weather URLs
function extractComune(locationName) {
  if (!locationName) return "";
  const beforeComma = (locationName.split(",")[0] || "").trim();
  const clean = beforeComma.replace(/\s*\([A-Za-z]{2}\)\s*$/g, "").trim();
  return (clean.split(/\s+/)[0] || clean).toLowerCase();
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function TreatmentCard({ t: item, onDelete }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const carenzaEnd = item.carenza_days && item.treatment_date
    ? new Date(new Date(item.treatment_date).getTime() + item.carenza_days * 86400000)
    : null;
  const daysLeft = carenzaEnd ? Math.ceil((carenzaEnd - new Date()) / 86400000) : null;
  return (
    <div className="p-3 rounded-lg vs-fade-up" style={{ background: "var(--surface)", border: "2px solid var(--border)" }}
      data-testid={`treatment-${item.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-base truncate" style={{ color: "var(--text-primary)" }}>{item.product_name}</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            <CalIcon size={12} className="inline mr-1" />
            {new Date(item.treatment_date).toLocaleDateString(dateLocale, { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
        <button onClick={onDelete} className="p-1.5 rounded" style={{ border: "1px solid var(--border)" }} aria-label={t("vineyard.treatmentCardDeleteAriaLabel")}>
          <Trash2 size={14} style={{ color: "var(--accent)" }} />
        </button>
      </div>
      {item.active_ingredient && (
        <div className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>
          <strong>P.A.:</strong> {item.active_ingredient}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(item.target_diseases || []).map((d, i) => <span key={i} className="vs-pill">{d}</span>)}
      </div>
      <div className="flex flex-wrap gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
        {item.dose && <span><strong>{t("treatment.doseLabel")}:</strong> {item.dose}</span>}
        {item.carenza_days != null && (
          <span>
            <strong>{t("vineyard.carenzaLabel")}:</strong> {t("vineyard.daysUnit", { count: item.carenza_days })}
            {daysLeft != null && (
              <span className="ml-1" style={{ color: daysLeft > 0 ? "var(--warning)" : "var(--primary)" }}>
                ({daysLeft > 0 ? t("vineyard.daysRemaining", { count: daysLeft }) : t("vineyard.carenzaEnded")})
              </span>
            )}
          </span>
        )}
        {item.next_treatment_date && (
          <span><strong>{t("treatment.nextTreatmentLabel")}:</strong> {new Date(item.next_treatment_date).toLocaleDateString(dateLocale)}</span>
        )}
      </div>
      {item.notes && <div className="mt-2 text-xs italic" style={{ color: "var(--text-muted)" }}>{item.notes}</div>}
    </div>
  );
}

function TreatmentForm({ vineyardId, vineyard, onClose, onCreated }) {
  const { t } = useTranslation();
  const isFR = vineyard?.country === "FR";
  const [form, setForm] = useState({
    product_name: "",
    treatment_date: new Date().toISOString().slice(0, 10),
    active_ingredient: "",
    target_diseases: "",
    dose: "",
    quantity_used: "",
    surface_treated: "",
    operator_name: "",
    carenza_days: "",
    next_treatment_date: "",
    phenology_phase: "",
    notes: "",
    siret_exploitant: "",
    siret_prestataire: "",
    ilot: "",
    numero_parcella: "",
    codice_insee_comune: "",
    bbch_stage: "",
    eppo_target_codes: "",
    treatment_start_time: "",
    treatment_end_time: "",
  });
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);

  const lookupProduct = async () => {
    if (!form.product_name.trim()) { toast.error(t("treatment.aiLookupMissingName")); return; }
    setLooking(true);
    try {
      const r = await axios.post(`${API}/products/lookup`,
        { product_name: form.product_name }, { withCredentials: true });
      const data = r.data;
      if (!data.found) {
        toast.warning(t("treatment.aiLookupNotFound"));
        return;
      }
      setForm((f) => ({
        ...f,
        active_ingredient: data.active_ingredient || f.active_ingredient,
        target_diseases: (data.target_diseases || []).join(", "),
        dose: data.dose || f.dose,
        carenza_days: data.carenza_days != null ? String(data.carenza_days) : f.carenza_days,
      }));
      toast.success(t("treatment.aiLookupRecognized", { product: data.product_name }));
    } catch (e) {
      toast.error(t("treatment.aiLookupError"));
    } finally { setLooking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name || !form.treatment_date) { toast.error(t("treatment.missingRequired")); return; }
    setSaving(true);
    try {
      const payload = {
        vineyard_id: vineyardId,
        product_name: form.product_name,
        treatment_date: form.treatment_date,
        active_ingredient: form.active_ingredient || null,
        target_diseases: form.target_diseases.split(",").map((s) => s.trim()).filter(Boolean),
        dose: form.dose || null,
        quantity_used: form.quantity_used || null,
        surface_treated: form.surface_treated ? parseFloat(form.surface_treated) : null,
        operator_name: form.operator_name || null,
        carenza_days: form.carenza_days ? parseInt(form.carenza_days) : null,
        next_treatment_date: form.next_treatment_date || null,
        phenology_phase: form.phenology_phase || null,
        notes: form.notes || null,
        siret_exploitant: form.siret_exploitant || null,
        siret_prestataire: form.siret_prestataire || null,
        ilot: form.ilot || null,
        numero_parcella: form.numero_parcella || null,
        codice_insee_comune: form.codice_insee_comune || null,
        bbch_stage: form.bbch_stage || null,
        eppo_target_codes: form.eppo_target_codes.split(",").map((s) => s.trim()).filter(Boolean),
        treatment_start_time: form.treatment_start_time || null,
        treatment_end_time: form.treatment_end_time || null,
      };
      const r = await axios.post(`${API}/treatments`, payload, { withCredentials: true });
      toast.success(t("treatment.saved"));
      onCreated(r.data);
    } catch { toast.error(t("treatment.saveError")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(17,20,17,0.5)" }} onClick={() => !saving && onClose()}>
      <div className="w-full sm:max-w-lg vs-card p-5"
        style={{ borderRadius: "16px 16px 0 0", maxHeight: "92vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()} data-testid="treatment-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading text-xl font-bold">{t("treatment.newTitle")}</h3>
          <button onClick={onClose} className="p-2"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="vs-label">{t("treatment.productNameLabel")}</label>
            <div className="flex gap-2">
              <input className="vs-input flex-1" value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                placeholder={t("treatment.productNamePlaceholder")} data-testid="form-product" required />
              <button type="button" onClick={lookupProduct} disabled={looking}
                className="vs-btn-accent inline-flex items-center gap-1 px-3"
                data-testid="lookup-btn" title={t("treatment.aiLookupTitle")}>
                <Wand2 size={16} /> {looking ? "..." : t("treatment.aiButtonLabel")}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              {t("treatment.aiHintPrefix")} <strong>{t("treatment.aiButtonLabel")}</strong> {t("treatment.aiHintSuffix")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vs-label">{t("treatment.dateLabel")}</label>
              <input type="date" className="vs-input" value={form.treatment_date}
                onChange={(e) => setForm({ ...form, treatment_date: e.target.value })}
                data-testid="form-date" required />
            </div>
            <div>
              <label className="vs-label">{t("treatment.carenzaLabel")}</label>
              <input type="number" className="vs-input" value={form.carenza_days}
                onChange={(e) => setForm({ ...form, carenza_days: e.target.value })}
                placeholder="20" data-testid="form-carenza" />
            </div>
          </div>
          <div>
            <label className="vs-label">{t("treatment.activeIngredientLabel")}</label>
            <input className="vs-input" value={form.active_ingredient}
              onChange={(e) => setForm({ ...form, active_ingredient: e.target.value })}
              placeholder="Metalaxyl-M + Mancozeb" data-testid="form-pa" />
          </div>
          <div>
            <label className="vs-label">{t("treatment.targetDiseasesLabel")}</label>
            <input className="vs-input" value={form.target_diseases}
              onChange={(e) => setForm({ ...form, target_diseases: e.target.value })}
              placeholder={t("treatment.targetDiseasesPlaceholder")} data-testid="form-diseases" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vs-label">{t("treatment.doseLabel")}</label>
              <input className="vs-input" value={form.dose}
                onChange={(e) => setForm({ ...form, dose: e.target.value })}
                placeholder="250 g/hl" data-testid="form-dose" />
            </div>
            <div>
              <label className="vs-label">{t("treatment.nextTreatmentLabel")}</label>
              <input type="date" className="vs-input" value={form.next_treatment_date}
                onChange={(e) => setForm({ ...form, next_treatment_date: e.target.value })}
                data-testid="form-next" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vs-label">{t("treatment.quantityLabel")}</label>
              <input className="vs-input" value={form.quantity_used}
                onChange={(e) => setForm({ ...form, quantity_used: e.target.value })}
                placeholder={t("treatment.quantityPlaceholder")} data-testid="form-qty" />
            </div>
            <div>
              <label className="vs-label">{t("treatment.surfaceLabel")}</label>
              <input type="number" step="0.01" className="vs-input" value={form.surface_treated}
                onChange={(e) => setForm({ ...form, surface_treated: e.target.value })}
                placeholder="0.5" data-testid="form-surface" />
            </div>
          </div>
          <div>
            <label className="vs-label">{t("treatment.operatorLabel")}</label>
            <input className="vs-input" value={form.operator_name}
              onChange={(e) => setForm({ ...form, operator_name: e.target.value })}
              placeholder={t("treatment.operatorPlaceholder")} data-testid="form-operator" />
          </div>
          <div>
            <label className="vs-label">{t("dashboard.form.phenologyLabel")}</label>
            <select className="vs-input" value={form.phenology_phase}
              onChange={(e) => setForm({ ...form, phenology_phase: e.target.value })}
              data-testid="form-phase">
              <option value="">—</option>
              {getPhenologyPhases(t).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {isFR && (
            <div className="space-y-3 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
                {t("treatment.fr.sectionTitle")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="vs-label">{t("treatment.fr.siretExploitantLabel")}</label>
                  <input className="vs-input" value={form.siret_exploitant}
                    onChange={(e) => setForm({ ...form, siret_exploitant: e.target.value })}
                    placeholder={t("treatment.fr.siretExploitantPlaceholder")} data-testid="form-siret-exploitant" />
                </div>
                <div>
                  <label className="vs-label">{t("treatment.fr.siretPrestataireLabel")}</label>
                  <input className="vs-input" value={form.siret_prestataire}
                    onChange={(e) => setForm({ ...form, siret_prestataire: e.target.value })}
                    placeholder={t("treatment.fr.siretPrestatairePlaceholder")} data-testid="form-siret-prestataire" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="vs-label">{t("treatment.fr.ilotLabel")}</label>
                  <input className="vs-input" value={form.ilot}
                    onChange={(e) => setForm({ ...form, ilot: e.target.value })}
                    data-testid="form-ilot" />
                </div>
                <div>
                  <label className="vs-label">{t("treatment.fr.parcelleLabel")}</label>
                  <input className="vs-input" value={form.numero_parcella}
                    onChange={(e) => setForm({ ...form, numero_parcella: e.target.value })}
                    data-testid="form-parcelle" />
                </div>
                <div>
                  <label className="vs-label">{t("treatment.fr.inseeLabel")}</label>
                  <input className="vs-input" value={form.codice_insee_comune}
                    onChange={(e) => setForm({ ...form, codice_insee_comune: e.target.value })}
                    placeholder={t("treatment.fr.inseePlaceholder")} data-testid="form-insee" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="vs-label">{t("treatment.fr.bbchLabel")}</label>
                  <input className="vs-input" value={form.bbch_stage}
                    onChange={(e) => setForm({ ...form, bbch_stage: e.target.value })}
                    placeholder={t("treatment.fr.bbchPlaceholder")} data-testid="form-bbch" />
                </div>
                <div>
                  <label className="vs-label">{t("treatment.fr.eppoLabel")}</label>
                  <input className="vs-input" value={form.eppo_target_codes}
                    onChange={(e) => setForm({ ...form, eppo_target_codes: e.target.value })}
                    placeholder={t("treatment.fr.eppoPlaceholder")} data-testid="form-eppo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="vs-label">{t("treatment.fr.startTimeLabel")}</label>
                  <input type="time" className="vs-input" value={form.treatment_start_time}
                    onChange={(e) => setForm({ ...form, treatment_start_time: e.target.value })}
                    data-testid="form-start-time" />
                </div>
                <div>
                  <label className="vs-label">{t("treatment.fr.endTimeLabel")}</label>
                  <input type="time" className="vs-input" value={form.treatment_end_time}
                    onChange={(e) => setForm({ ...form, treatment_end_time: e.target.value })}
                    data-testid="form-end-time" />
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="vs-label">{t("treatment.notesLabel")}</label>
            <textarea className="vs-input" rows="2" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t("treatment.notesPlaceholder")} data-testid="form-notes" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" className="vs-btn-secondary flex-1" onClick={onClose} disabled={saving}>{t("dashboard.form.cancelButton")}</button>
            <button type="submit" className="vs-btn-primary flex-1" disabled={saving} data-testid="submit-treatment">
              {saving ? t("dashboard.form.submitSaving") : t("treatment.submitButton")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
